import React, { useState } from "react";
import { Brain, Check, X, RefreshCw } from "lucide-react";
import api from "@/lib/api";

export default function KnowledgeCheck({ questions, locked, lastResult, onSubmitted }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(lastResult || null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id] !== undefined);

  const submit = async () => {
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/knowledge-check/submit", {
        answers: questions.map((q) => ({ question_id: q.id, answer_index: answers[q.id] })),
      });
      setResult(data.result);
      onSubmitted?.();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setResult(null);
  };

  if (locked) {
    return (
      <div className="bloom-card p-10 text-center bloom-locked" data-testid="knowledge-check-locked">
        <Brain className="mx-auto mb-3 text-[var(--bloom-ink-muted)]" strokeWidth={1.5} />
        <h3 className="font-serif text-2xl mb-2">Knowledge Check</h3>
        <p className="text-sm text-[var(--bloom-ink-soft)]">Complete all 16 prospectus chapters to unlock.</p>
      </div>
    );
  }

  if (result) {
    return (
      <div className="bloom-card p-8 md:p-10" data-testid="knowledge-check-result">
        <div className="flex items-center gap-3 mb-6">
          <Brain className="text-[var(--bloom-brand)]" strokeWidth={1.5} />
          <h3 className="font-serif text-2xl">Knowledge Check</h3>
        </div>
        <div className="text-center py-8">
          <div className={`font-serif text-7xl mb-2 ${result.passed ? "text-[var(--bloom-brand)]" : "text-[var(--bloom-accent)]"}`} data-testid="knowledge-check-score">
            {result.score}%
          </div>
          <div className="text-sm tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">
            {result.correct} of {result.total} correct
          </div>
          {result.passed ? (
            <p className="mt-6 font-serif italic text-lg text-[var(--bloom-ink-soft)] max-w-md mx-auto">
              Beautifully done. You understand the Bloom Club. Move on to the final acknowledgement below to unlock Stage 3.
            </p>
          ) : (
            <p className="mt-6 font-serif italic text-lg text-[var(--bloom-accent)] max-w-md mx-auto">
              Not quite. A 100% score is required. Please revisit the prospectus and try again.
            </p>
          )}
        </div>

        {/* Per-question result */}
        <div className="space-y-3 mt-8 border-t border-[var(--bloom-border)] pt-8">
          {questions.map((q, i) => (
            <div key={q.id} className="flex items-start gap-3 text-sm">
              {result.per_question[q.id] ? (
                <Check size={16} className="text-[var(--bloom-brand)] mt-1 shrink-0" />
              ) : (
                <X size={16} className="text-[var(--bloom-accent)] mt-1 shrink-0" />
              )}
              <span className="text-[var(--bloom-ink-soft)]">Q{i + 1}. {q.question}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-8">
          <button onClick={reset} className="bloom-btn bloom-btn-outline" data-testid="knowledge-check-retry-btn">
            <RefreshCw size={14} /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bloom-card p-8 md:p-10" data-testid="knowledge-check-quiz">
      <div className="flex items-center gap-3 mb-3">
        <Brain className="text-[var(--bloom-brand)]" strokeWidth={1.5} />
        <h3 className="font-serif text-2xl">Knowledge Check</h3>
      </div>
      <p className="text-sm text-[var(--bloom-ink-soft)] mb-10 max-w-xl">
        Five short questions to confirm you have read the prospectus carefully.
        A perfect score (100%) is required before you may proceed to Stage 3.
      </p>

      <div className="space-y-10">
        {questions.map((q, qi) => (
          <div key={q.id} data-testid={`knowledge-question-${qi + 1}`}>
            <div className="flex items-baseline gap-3 mb-4">
              <span className="font-serif text-2xl text-[var(--bloom-brand)]">{String(qi + 1).padStart(2, "0")}</span>
              <h4 className="font-serif text-xl leading-snug">{q.question}</h4>
            </div>
            <div className="space-y-2 pl-10">
              {q.options.map((opt, oi) => {
                const selected = answers[q.id] === oi;
                return (
                  <label
                    key={`${q.id}-${oi}`}
                    className={`flex items-start gap-3 p-3 cursor-pointer border transition ${
                      selected
                        ? "border-[var(--bloom-brand)] bg-[var(--bloom-surface)]"
                        : "border-transparent hover:border-[var(--bloom-border)]"
                    }`}
                    data-testid={`knowledge-q${qi + 1}-option-${oi}`}
                  >
                    <input
                      type="radio"
                      name={q.id}
                      checked={selected}
                      onChange={() => setAnswers({ ...answers, [q.id]: oi })}
                      className="mt-1"
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {err && <div className="mt-6 text-sm text-[var(--bloom-accent)] font-serif italic">{err}</div>}

      <div className="mt-10 flex justify-end">
        <button
          disabled={!allAnswered || submitting}
          onClick={submit}
          className="bloom-btn bloom-btn-primary"
          data-testid="knowledge-check-submit-btn"
        >
          {submitting ? "Marking…" : "Submit Knowledge Check"}
        </button>
      </div>
    </div>
  );
}
